import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DownloadAllResponse, ShareMetadata, SingleFileDownloadResponse, MultipleFilesDownloadResponse, Pagination, SharesListResponse } from '@df-gate-share/shared';
import { apiClient } from '../lib/api';
import Header from '../components/Header';
import MessageDialog from '../components/MessageDialog';
import AnnouncementBanner from '../components/AnnouncementBanner';
import { useMessageDialog } from '../hooks/useMessageDialog';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

type StatusFilter = 'all' | 'active' | 'expired' | 'revoked';
type SortOrder = 'desc' | 'asc';

const FileList: React.FC = () => {
  const navigate = useNavigate();
  const { dialogState, closeDialog, showAlert, showConfirm } = useMessageDialog();
  const [shares, setShares] = useState<ShareMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>({
    hasMore: false,
    count: 0
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number; above: boolean } | null>(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleTooltipEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const text = e.currentTarget.getAttribute('data-tooltip');
    if (!text) return;
    clearTimeout(tooltipTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < 120;
    setTooltip({
      text,
      x: rect.left,
      y: above ? rect.top : rect.bottom + 4,
      above,
    });
  }, []);

  const handleTooltipLeave = useCallback(() => {
    tooltipTimeoutRef.current = setTimeout(() => setTooltip(null), 100);
  }, []);

  useEffect(() => {
    fetchShares();
  }, [statusFilter, sortOrder]);

  const fetchShares = async (cursor?: string, resetHistory: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('sortOrder', sortOrder);
      if (cursor) params.append('cursor', cursor);
      params.append('limit', '50');

      const response = await apiClient.get(`/api/v1/shares?${params.toString()}`);
      const data: SharesListResponse = response.data;
      setShares(data.shares || []);
      setPagination(data.pagination || { hasMore: false, count: 0 });

      if (resetHistory) {
        setCursorHistory([]);
      }
    } catch (err) {
      setError('エラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(pagination.nextCursor);
      fetchShares(pagination.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const prevCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setCurrentCursor(prevCursor);
      fetchShares(prevCursor);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysRemaining = (expiresAt: string): { days: number; text: string; isExpired: boolean; className: string } => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { days: diffDays, text: '期限切れ', isExpired: true, className: 'expired' };
    } else if (diffDays <= 3) {
      return { days: diffDays, text: `あと${diffDays}日`, isExpired: false, className: 'warning' };
    } else {
      return { days: diffDays, text: `あと${diffDays}日`, isExpired: false, className: 'ok' };
    }
  };

  const formatRecipientsDisplay = (recipients: string[]): React.ReactNode => {
    if (recipients.length === 0) return '';
    return (
      <div className="cell-truncate" data-tooltip={recipients.join('\n')}
        onMouseEnter={handleTooltipEnter} onMouseLeave={handleTooltipLeave}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {recipients.length > 1 && (
            <span style={{
              display: 'inline-block',
              background: '#f0fdf4',
              color: '#166534',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              width: 'fit-content'
            }}>
              {recipients.length}名
            </span>
          )}
          <div style={{
            fontSize: '13px',
            color: '#6b7280',
            maxWidth: '250px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {recipients.join(', ')}
          </div>
        </div>
      </div>
    );
  };

  const handleDownload = async (shareId: string, title?: string) => {
    try {
      const response = await apiClient.get(`/api/v1/shares/${shareId}/download-all`);
      const data = response.data as DownloadAllResponse;

      if (data.fileCount === 1) {
        // Single file - direct download
        window.open((data as SingleFileDownloadResponse).downloadUrl, '_blank');
      } else {
        // Multiple files - create zip archive
        const multiData = data as MultipleFilesDownloadResponse;
        if (multiData.files.length === 0) {
          setError('ダウンロード可能なファイルがありません');
          setTimeout(() => setError(''), 3000);
          return;
        }

        // Create zip archive
        const zip = new JSZip();

        // Download all files and add to zip
        for (const file of multiData.files) {
          try {
            const fileResponse = await fetch(file.downloadUrl);
            if (!fileResponse.ok) {
              throw new Error(`Failed to download ${file.fileName}`);
            }
            const blob = await fileResponse.blob();
            zip.file(file.fileName, blob);
          } catch (err) {
            console.error(`Error downloading ${file.fileName}:`, err);
            setError(`${file.fileName} のダウンロードに失敗しました`);
            setTimeout(() => setError(''), 3000);
            return;
          }
        }

        // Generate zip file and trigger download
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipFileName = title ? `${title}.zip` : 'files.zip';
        saveAs(zipBlob, zipFileName);
      }

      setSuccessMessage('ダウンロードを開始しました');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('ダウンロードに失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = (shareId: string, title: string) => {
    showConfirm(
      '共有の削除',
      `「${title}」を削除してもよろしいですか？`,
      async () => {
        closeDialog();
        try {
          await apiClient.put(`/api/v1/shares/${shareId}/revoke`);
          setSuccessMessage('共有を削除しました');
          setTimeout(() => setSuccessMessage(''), 3000);
          fetchShares();
        } catch (err) {
          setError('削除に失敗しました');
          setTimeout(() => setError(''), 3000);
        }
      },
      { confirmLabel: '削除', confirmClassName: 'btn btn--danger' }
    );
  };

  return (
    <>
      <Header />
      <div className="page-wrapper">
        <div className="page-container">
          {/* お知らせバナー（未読があるときのみ表示） */}
          <AnnouncementBanner />

          {/* Page Header */}
          <h1 className="page-title">
            ファイル一覧
          </h1>

          {/* Action Bar */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card__body">
              <button
                onClick={() => navigate('/upload')}
                className="btn btn--primary"
              >
                <span className="material-symbols-outlined icon-sm">upload</span>
                ファイルを送信
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert--error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert--success">
              {successMessage}
            </div>
          )}

          {/* Filters */}
          <div className="filter-bar">
            <div className="filter-group">
              <label className="filter-label">
                ステータス:
              </label>
              <div className="filter-group">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`filter-btn${statusFilter === 'all' ? ' filter-btn--active' : ''}`}
                >
                  すべて
                </button>
                <button
                  onClick={() => setStatusFilter('active')}
                  className={`filter-btn${statusFilter === 'active' ? ' filter-btn--active' : ''}`}
                >
                  アクティブ
                </button>
                <button
                  onClick={() => setStatusFilter('expired')}
                  className={`filter-btn${statusFilter === 'expired' ? ' filter-btn--active' : ''}`}
                >
                  期限切れ
                </button>
                <button
                  onClick={() => setStatusFilter('revoked')}
                  className={`filter-btn${statusFilter === 'revoked' ? ' filter-btn--active' : ''}`}
                >
                  削除済み
                </button>
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                並び替え:
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="form-select"
              >
                <option value="desc">新しい順</option>
                <option value="asc">古い順</option>
              </select>
            </div>
          </div>

          {/* File List */}
          {loading ? (
            <div className="empty-state">
              読み込み中...
            </div>
          ) : !shares || shares.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon"><span className="material-symbols-outlined icon-xl">drafts</span></div>
              <div className="empty-state__title">
                送信履歴がありません
              </div>
              <div className="empty-state__desc">
                ファイルを送信すると、ここに送信履歴が表示されます
              </div>
            </div>
          ) : (
            <>
              <div className="card">
                <div className="table-wrapper table-wrapper--responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>タイトル</th>
                        <th>ファイル</th>
                        <th>送信先</th>
                        <th>サイズ</th>
                        <th>送信日時</th>
                        <th>有効期限</th>
                        <th>ステータス</th>
                        <th>アクション</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shares.map((share) => {
                        const expiryInfo = calculateDaysRemaining(share.expiresAt);
                        const isInactive = share.status === 'expired' || share.status === 'revoked';

                        return (
                          <tr
                            key={share.shareId}
                            className={isInactive ? 'row--inactive' : ''}
                          >
                            <td data-label="タイトル">
                              <div style={{ fontWeight: 600, color: '#111827' }}>
                                {share.title}
                              </div>
                            </td>
                            <td data-label="ファイル">
                              <div className="cell-truncate" data-tooltip={(share.fileNames || share.fileIds).join('\n')}
                                onMouseEnter={handleTooltipEnter} onMouseLeave={handleTooltipLeave}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {share.fileCount > 1 && (
                                    <span style={{
                                      display: 'inline-block',
                                      background: '#eff6ff',
                                      color: '#1e40af',
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      width: 'fit-content'
                                    }}>
                                      {share.fileCount}ファイル
                                    </span>
                                  )}
                                  <div style={{
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    maxWidth: '250px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {share.fileNames?.join(', ') || share.fileIds.join(', ')}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td data-label="送信先">
                              {formatRecipientsDisplay(share.recipients)}
                            </td>
                            <td data-label="サイズ">
                              {formatFileSize(share.totalSize)}
                            </td>
                            <td data-label="送信日時">
                              {formatDate(share.createdAt)}
                            </td>
                            <td data-label="有効期限">
                              <span className={`expiry-${expiryInfo.className}`}>
                                {expiryInfo.text}
                              </span>
                            </td>
                            <td data-label="ステータス">
                              <span className={
                                share.status === 'active' ? 'badge badge--active' :
                                share.status === 'expired' ? 'badge badge--expired' : 'badge badge--revoked'
                              }>
                                {share.status === 'active' ? 'アクティブ' :
                                 share.status === 'expired' ? '期限切れ' : '削除済み'}
                              </span>
                            </td>
                            <td data-label="アクション">
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => navigate(`/shares/${share.shareId}`)}
                                  title="詳細"
                                  className="btn btn--sm btn--outline-success"
                                >
                                  詳細
                                </button>
                                {!isInactive && (
                                  <button
                                    onClick={() => handleDownload(share.shareId, share.title)}
                                    title="ダウンロード"
                                    className="btn btn--sm btn--outline-cyan"
                                  >
                                    ダウンロード
                                  </button>
                                )}
                                {share.status !== 'revoked' && (
                                  <button
                                    onClick={() => handleDelete(share.shareId, share.title)}
                                    title="削除"
                                    className="btn btn--sm btn--outline-danger"
                                  >
                                    削除
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {shares.length > 0 && (
                  <div className="pagination">
                    <div className="pagination__info">
                      {pagination.count}件の結果を表示中
                    </div>
                    <div className="pagination__buttons">
                      <button
                        onClick={handlePrevPage}
                        disabled={cursorHistory.length === 0}
                        className="btn btn--secondary"
                      >
                        ← 前へ
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={!pagination.hasMore}
                        className="btn btn--secondary"
                      >
                        次へ →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {tooltip && (
        <div
          className="cell-tooltip"
          style={{
            left: tooltip.x,
            ...(tooltip.above
              ? { bottom: window.innerHeight - tooltip.y + 4 }
              : { top: tooltip.y }),
          }}
        >
          {tooltip.text}
        </div>
      )}

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </>
  );
};

export default FileList;
